import { LoggedInUserStore } from "../loggedInUser/LoggedInUserStore";
import { action, computed, makeObservable, observable } from "mobx";
import { inject, injectable } from "../injection";
import { matchPath } from "react-router-dom";
import {
    IRoute,
    mainNavRoutes,
    settingsRoute,
    adminRoute,
    userRoute,
    appendRoutes
} from "./routes";
import { computedFn } from "mobx-utils/lib/computedFn";
import { ReactNode } from "react";

export interface IMenuRoute extends IRoute {
    originalRoute?: IRoute;
}

@injectable("singleton")
export class RouteStore {
    @observable.shallow private accessTokens = new Set<IRoute>();
    @observable.shallow public accessOverlay: ReactNode | undefined = undefined;

    constructor(
        @inject(LoggedInUserStore) private loggedInUserStore: LoggedInUserStore
    ) {
        makeObservable(this);
    }

    @computed
    get flattenedRoutes(): IRoute[] {
        const allRoutes = [
            ...mainNavRoutes,
            settingsRoute,
            adminRoute,
            userRoute,
            ...appendRoutes
        ];

        let flattened: IRoute[] = [];
        for (const route of allRoutes) {
            flattened = [...flattened, ...this.flattenRoute(route, false)];
        }
        return flattened;
    }

    private flattenRoute(
        route: IRoute,
        isParentPermissioned: boolean
    ): IRoute[] {
        let flattened: IRoute[] = [];
        if (this.descendingIsRoutePermissioned(route, isParentPermissioned)) {
            flattened.push(route);
            if (route.children) {
                for (const child of route.children) {
                    flattened = [
                        ...flattened,
                        ...this.flattenRoute(child, true)
                    ];
                }
            }
        }
        return flattened;
    }

    private descendingIsRoutePermissioned(
        route: IRoute,
        isParentPermissioned: boolean
    ): boolean {
        if (route.onlyShowIfThereArePermissionedChildren) {
            return (
                route.children?.some(x =>
                    this.descendingIsRoutePermissioned(x, false)
                ) ?? false
            );
        } else if (route.isPermissoned === undefined) {
            return isParentPermissioned;
        } else {
            return route.isPermissoned(this.loggedInUserStore);
        }
    }

    public getRouteByPath = computedFn((path: string): IRoute | undefined => {
        return this.flattenedRoutes.find(
            x =>
                x.path !== undefined &&
                matchPath(path, { path: x.path, exact: x.pathExact })
        );
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getParamsByPath = computedFn((path: string): any => {
        for (const route of this.flattenedRoutes) {
            if (route.path !== undefined) {
                const match = matchPath(path, {
                    path: route.path,
                    exact: route.pathExact
                });
                if (match) {
                    return match.params;
                }
            }
        }
        return {};
    });

    public getMenuRoutes = computedFn((routes: IRoute[]): IMenuRoute[] => {
        return this.calcMenuRoutes(routes);
    });

    public getChildrenMenuRoutes = computedFn(
        (route: IRoute | undefined): IMenuRoute[] => {
            return route ? this.calcMenuRoutes(route.children ?? []) : [];
        }
    );

    private calcMenuRoutes(routes: IRoute[]): IMenuRoute[] {
        const menuRoutes: IMenuRoute[] = [];
        let stack = [...routes];
        while (stack.length > 0) {
            const route = stack.shift();
            if (!route) {
                break;
            }
            if (!this.getIsRoutePermissioned(route)) {
                continue;
            }
            if (route.useFirstChildForMenus) {
                const firstChild = this.findFirstChild(route);
                if (firstChild) {
                    menuRoutes.push({
                        ...firstChild,
                        title: route.title ?? firstChild.title,
                        description:
                            route.description ?? firstChild.description,
                        originalRoute: route
                    });
                }
            } else if (route.path !== undefined) {
                menuRoutes.push(route);
            }
            if (route.spreadChildrenForMenus && route.children) {
                stack = [...route.children, ...stack];
            }
        }
        return menuRoutes;
    }

    private findFirstChild(route: IRoute): IRoute | undefined {
        let found: IRoute | undefined;
        if (route.children) {
            let stack = [...route.children];
            while (stack.length > 0) {
                const child = stack.shift();
                if (!child) {
                    break;
                }
                if (!this.getIsRoutePermissioned(child)) {
                    continue;
                }
                if (child.useFirstChildForMenus) {
                    const firstChild = this.findFirstChild(child);
                    if (firstChild) {
                        found = firstChild;
                        break;
                    }
                } else if (child.path !== undefined) {
                    found = child;
                    break;
                }
                if (child.spreadChildrenForMenus && child.children) {
                    stack = [...child.children, ...stack];
                }
            }
        }
        return found;
    }

    public getTopAncestor = computedFn(
        (
            route: IRoute | undefined,
            stopBefore: IRoute | undefined
        ): IRoute | undefined => {
            const parent = this.flattenedRoutes.find(x =>
                x.children?.find(y => y === route)
            );

            if (parent?.spreadChildrenForMenus) {
                const grandParent = this.flattenedRoutes.find(x =>
                    x.children?.find(y => y === parent)
                );
                if (!grandParent || grandParent === stopBefore) {
                    return route;
                }
            }

            if (parent === stopBefore) {
                return route;
            }

            return parent ? this.getTopAncestor(parent, stopBefore) : route;
        }
    );

    public getIsRoutePermissioned = computedFn((route: IRoute): boolean => {
        return !!this.flattenedRoutes.find(x => x === route);
    });

    public getRouteTitle(route: IRoute): string | undefined {
        return typeof route.title === "string"
            ? route.title
            : route.title?.(this.loggedInUserStore);
    }

    public getRouteDescription(route: IRoute): string | undefined {
        return typeof route.description === "string"
            ? route.description
            : route.description?.(this.loggedInUserStore);
    }

    public hasAccess(route: IRoute | undefined): boolean {
        if (!route) {
            return false;
        }
        if (!route.shouldAllowAccess) {
            return true;
        }
        if (this.accessTokens.has(route)) {
            return true;
        }

        // sync access check (to avoid briefly showing no access warning)
        const access = route.shouldAllowAccess(
            this.loggedInUserStore,
            undefined,
            undefined,
            () => undefined
        );
        if (access.grantAccess) {
            return true;
        }

        return false;
    }

    @action
    public grantAccess = (route: IRoute) => {
        if (!this.accessTokens.has(route)) {
            this.accessTokens.add(route);
        }
    };

    @action
    public revokeAccess = (route: IRoute) => {
        this.accessTokens.delete(route);
    };

    @action
    public setAccessOverlay = (overlay: ReactNode | undefined) => {
        this.accessOverlay = overlay;
    };

    @action
    public dismissAccessOverlay = (route: IRoute, grantAccess: boolean) => {
        if (grantAccess) {
            this.grantAccess(route);
        }
        this.accessOverlay = undefined;
    };

    public triggerAccessCheck(
        route: IRoute,
        path: string | undefined,
        target: string | undefined
    ): void {
        if (route.shouldAllowAccess) {
            const access = route.shouldAllowAccess(
                this.loggedInUserStore,
                path,
                target,
                grantAccess => {
                    this.dismissAccessOverlay(
                        route,
                        target !== undefined ? false : grantAccess // always reject access if opening in a new target
                    );
                    if (path === undefined && !grantAccess) {
                        // dismissed while on no-access page. forward to home
                        document.location.hash = "/";
                    }
                }
            );
            if (access.grantAccess) {
                this.grantAccess(route);
            }
            if (access.overlay) {
                this.setAccessOverlay(access.overlay);
            }
        }
    }
}

export function getTestId(route: IRoute): string {
    return route.path?.substring(1) ?? "";
}

export function pathToHref(path: string): string {
    return `#${path}`;
}
